/**
 * IntakeContext — single source of truth for session + state across the SPA.
 *
 * Wraps a useReducer. The reducer's job is pure state transitions. Side
 * effects (localStorage persistence, API calls) live in the provider and in
 * hooks that consume the context.
 *
 * State merge semantics for `MERGE_DELTA` match ADR-006 / pipeline-spec:
 *   - confirmed: spread (Worker adds new keys; never removes)
 *   - required_pending / optional_pending: REPLACE (they only shrink)
 *   - history: REPLACE when present in delta (Worker compressed); otherwise
 *     untouched. History appends happen via explicit APPEND_HISTORY actions.
 *   - language: REPLACE when present in delta (detect_language resolved)
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { HistoryTurn, IntakeState, Language } from '../api/types';
import {
  INITIAL_STATE,
  loadOrCreateSessionId,
  loadPhase,
  loadState,
  resetSession,
  savePhase,
  saveState,
} from './session';

/** High-level screen the user is on. Derived client-side; not part of the
 *  wire contract. Persists in localStorage so a page refresh lands on the
 *  same screen. */
export type Phase = 'welcome' | 'upload' | 'chat' | 'review' | 'submitted';

export type IntakeAction =
  | { type: 'MERGE_DELTA'; delta: Partial<IntakeState> }
  | { type: 'APPEND_HISTORY'; turn: HistoryTurn }
  | { type: 'SET_LANGUAGE'; language: Language }
  | { type: 'RESET'; state: IntakeState };

/** Exported for unit tests. Pure function — no side effects. */
export function intakeReducer(state: IntakeState, action: IntakeAction): IntakeState {
  switch (action.type) {
    case 'MERGE_DELTA': {
      const d = action.delta;
      return {
        confirmed:
          d.confirmed !== undefined
            ? { ...state.confirmed, ...d.confirmed }
            : state.confirmed,
        required_pending:
          d.required_pending !== undefined
            ? d.required_pending
            : state.required_pending,
        optional_pending:
          d.optional_pending !== undefined
            ? d.optional_pending
            : state.optional_pending,
        history: d.history !== undefined ? d.history : state.history,
        language: d.language !== undefined ? d.language : state.language,
      };
    }
    case 'APPEND_HISTORY':
      return { ...state, history: [...state.history, action.turn] };
    case 'SET_LANGUAGE':
      return { ...state, language: action.language };
    case 'RESET':
      return action.state;
    default:
      return state;
  }
}

interface IntakeContextValue {
  sessionId: string;
  state: IntakeState;
  phase: Phase;
  mergeDelta: (delta: Partial<IntakeState>) => void;
  appendHistory: (turn: HistoryTurn) => void;
  setLanguage: (language: Language) => void;
  setPhase: (phase: Phase) => void;
  resetAll: () => void;
}

const IntakeContext = createContext<IntakeContextValue | null>(null);

interface ProviderProps {
  children: ReactNode;
}

export function IntakeProvider({ children }: ProviderProps) {
  // Initialize session + state lazily so SSR or test envs without
  // localStorage don't blow up at import time.
  const sessionIdRef = useRef<string>(loadOrCreateSessionId());
  const [state, dispatch] = useReducer(
    intakeReducer,
    sessionIdRef.current,
    (id) => loadState(id) ?? INITIAL_STATE,
  );
  const [phase, setPhaseInternal] = useState<Phase>(
    () => loadPhase(sessionIdRef.current) ?? 'welcome',
  );

  // Persist on every state change.
  useEffect(() => {
    saveState(sessionIdRef.current, state);
  }, [state]);

  // Persist phase independently so refresh lands on the same screen.
  useEffect(() => {
    savePhase(sessionIdRef.current, phase);
  }, [phase]);

  // Keep <html lang> in sync with active language for screen readers.
  useEffect(() => {
    if (state.language) {
      document.documentElement.setAttribute('lang', state.language);
    }
  }, [state.language]);

  const setPhase = useCallback((next: Phase) => setPhaseInternal(next), []);

  const value = useMemo<IntakeContextValue>(
    () => ({
      sessionId: sessionIdRef.current,
      state,
      phase,
      mergeDelta: (delta) => dispatch({ type: 'MERGE_DELTA', delta }),
      appendHistory: (turn) => dispatch({ type: 'APPEND_HISTORY', turn }),
      setLanguage: (language) => dispatch({ type: 'SET_LANGUAGE', language }),
      setPhase,
      resetAll: () => {
        sessionIdRef.current = resetSession(sessionIdRef.current);
        dispatch({ type: 'RESET', state: INITIAL_STATE });
        setPhaseInternal('welcome');
      },
    }),
    [state, phase, setPhase],
  );

  return <IntakeContext.Provider value={value}>{children}</IntakeContext.Provider>;
}

export function useIntake(): IntakeContextValue {
  const ctx = useContext(IntakeContext);
  if (!ctx) {
    throw new Error('useIntake must be used within <IntakeProvider>');
  }
  return ctx;
}
